import React from 'react';
import { COMPOSITE_KIND_ICON, rowState, STATE_CHIP } from '@repo/ui';

import { TitleBarSearch } from './TitleBarSearch';
import type { TitleBarSearchTypeStatus } from './TitleBarSearch';
import type { CompositeSearchHit, TotalsReadState } from './compositeSearch';

/**
 * The Composite measures step's title-bar search (`composites-page-search`, spec FR4): the shared
 * `TitleBarSearch` shell — the same box, keys and announcements as the Floor plan and Observers
 * steps — bound to TOTALS. A hit is a total, found by its own target or through a source
 * (`compositeSearch.ts`); each reads as its row does: the row's glyph and name, its code, the
 * sources that matched, and its state in the list's own words.
 */

export interface CompositeTotalSearchProps {
  /** What the query finds — `matchCompositeTotals` over the step's own entries and totals. */
  match: (query: string) => { hits: CompositeSearchHit[]; total: number };
  /** The list holds no totals at all — "no totals yet" is not "nothing matches". */
  noTotals: boolean;
  /**
   * Whether the list's totals are all in (`totalsReadState`). While the running configuration is
   * read, or after that read failed, an empty result is not a claim that nothing exists.
   */
  readState?: TotalsReadState;
  onPick: (hit: CompositeSearchHit) => void;
  onLoadTypes?: () => void;
  typeStatus?: TitleBarSearchTypeStatus;
}

const LABEL = 'Search totals';
const PLACEHOLDER = 'Search totals…';

const keyOf = (hit: CompositeSearchHit) => hit.row.refid;

/** "counts Benches", or "counts Benches and 1 more" when more than one source matched. */
export const countsLine = (matched: ReadonlyArray<string>): string =>
  matched.length === 0
    ? ''
    : matched.length === 1
      ? `counts ${matched[0]}`
      : `counts ${matched[0]} and ${matched.length - 1} more`;

/**
 * The list's sentence when nothing is found. "No totals yet" and "nothing matches" are claims
 * about the twin, so they are made only once its totals are all in; until then the sentence says
 * the read is unfinished or failed instead.
 */
export const emptyText = (query: string, noTotals: boolean, readState: TotalsReadState): string => {
  if (readState === 'reading')
    return noTotals
      ? 'This twin’s own totals are still being read.'
      : `Nothing matches “${query}” yet — this twin’s own totals are still being read.`;
  if (readState === 'failed')
    return noTotals
      ? 'This twin’s own totals could not be read, so none can be searched.'
      : `Nothing matches “${query}” — and this twin’s own totals could not be read, so some may be missing.`;
  return noTotals ? 'This twin has no totals yet.' : `No total on this twin matches “${query}”.`;
};

export function CompositeTotalSearch({
  match,
  noTotals,
  readState = 'settled',
  onPick,
  onLoadTypes,
  typeStatus,
}: CompositeTotalSearchProps) {
  const option = React.useCallback((hit: CompositeSearchHit) => {
    const { row } = hit;
    const state = STATE_CHIP[rowState(row)].text;
    const counts = hit.via === 'source' ? countsLine(hit.matched) : '';
    return {
      icon: COMPOSITE_KIND_ICON[row.kind],
      label: row.target.label,
      meta: [row.code, counts].filter(Boolean).join(' · '),
      trailing: state,
      // The code is in the name: two totals both called "Rack 6" are told apart by it, as they are
      // on screen (the name replaces the visible meta line for a screen reader).
      name: [
        row.target.label,
        row.code,
        state,
        hit.via === 'source' ? `counts ${hit.matched.join(', ')}` : null,
      ]
        .filter(Boolean)
        .join(', '),
    };
  }, []);
  return (
    <TitleBarSearch<CompositeSearchHit>
      label={LABEL}
      placeholder={PLACEHOLDER}
      match={match}
      keyOf={keyOf}
      option={option}
      emptyText={query => emptyText(query, noTotals, readState)}
      pickedText={hit =>
        hit.row.automatic
          ? `Showing the automatic total for ${hit.row.target.label}.`
          : `Opened the total for ${hit.row.target.label}.`
      }
      onPick={onPick}
      onLoadTypes={onLoadTypes}
      typeStatus={typeStatus}
    />
  );
}
